import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Typography, ErrorBoundary } from '@vp/swan';
import { forwardRef, useMemo } from 'react';
import { UnitsCountInternal } from '../units-count/units-count.component.js';
import { clsx } from '../../external/clsx/dist/clsx.js';
import styles from './unit-price-with-quantity.module.scss.js';
import { useLocalization } from '../../hooks/useLocalizations.js';
import { formatCurrency } from '../../utils/formatter.util.js';
import { UnitPriceInternal } from '../unit-price/unit-price.component.js';
import { PriceSummaryInternal } from '../price-summary/price-summary.component.js';

const UnitPriceWithQuantityInternal = forwardRef(({ className, unitPriceConfig, placeholdertext, ...rest }, ref) => {
  const {
    locale,
    currency,
    minimumFractionDigits,
    quantity,
    unitListPrice,
    unitDiscountedPrice,
    vatInc = false,
    shippingIncl = false,
    setUpFee,
    showPriceSummary = false,
    basePrice,
    discountedBasePrice,
    totalListPrice,
    totalDiscountedPrice,
    discountedSetUpFee
  } = unitPriceConfig;
  const { t } = useLocalization(locale);
  const classNames = clsx(className, styles.unitPriceWithQuantity);
  const getPrice = (price) => price?.[vatInc ? "taxed" : "untaxed"] ?? 0;
  const unitPriceValue = getPrice(unitDiscountedPrice) ?? getPrice(unitListPrice);
  const unitPrice = useMemo(() => {
    if (!unitPriceValue) return null;
    return formatCurrency(locale, currency, minimumFractionDigits, unitPriceValue);
  }, [locale, currency, minimumFractionDigits, unitPriceValue]);
  if (quantity === 1 || unitPriceValue === 0) {
    return /* @__PURE__ */ jsx(UnitsCountInternal, { placeholdertext, ref, className: classNames, quantity, locale, ...rest });
  }
  if (quantity <= 0 && unitListPrice && unitDiscountedPrice) {
    return /* @__PURE__ */ jsx(
      UnitPriceInternal,
      {
        placeholdertext,
        ref,
        className: classNames,
        unitPriceConfig: { ...unitPriceConfig, unitListPrice, unitDiscountedPrice },
        ...rest
      }
    );
  }
  return /* @__PURE__ */ jsxs(Typography, { as: "span", ref, className: classNames, fontSkin: "body-small", textColor: "subtle", ...rest, children: [
    t("unitPriceWithQuantity", { pricePerUnit: unitPrice, unitsCount: quantity }),
    showPriceSummary && setUpFee && basePrice && discountedBasePrice && totalListPrice && totalDiscountedPrice && discountedSetUpFee && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Typography, { as: "span", fontSkin: "body-small-bold", marginX: "2", children: "\xB7" }),
      /* @__PURE__ */ jsx(
        PriceSummaryInternal,
        {
          priceContext: {
            locale,
            currency,
            minimumFractionDigits,
            vatInc,
            shippingIncl
          },
          basePrice,
          discountedBasePrice,
          totalListPrice,
          totalDiscountedPrice,
          setUpFee,
          discountedSetUpFee,
          placeholdertext
        }
      )
    ] })
  ] });
});
const UnitPriceWithQuantity = forwardRef((props, ref) => {
  return /* @__PURE__ */ jsx(
    ErrorBoundary,
    {
      fallback: /* @__PURE__ */ jsx(Typography, { as: "span", fontSkin: "body-small", textColor: "subtle", children: props.placeholdertext }),
      children: /* @__PURE__ */ jsx(UnitPriceWithQuantityInternal, { ...props, ref })
    }
  );
});

export { UnitPriceWithQuantity, UnitPriceWithQuantityInternal };
